import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Camera, Edit2, Check, X, Lock, Globe, AlertCircle, Unlock, Settings, Tag, Plus } from 'lucide-react';
import ClickableTag from '../../components/ui/ClickableTag';
import AccountSettingsModal from './AccountSettingsModal';

export default function UserProfileHeader({ user }) {
  const { updateUserProfile, uploadProfilePhoto } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    username: user.username,
    display_name: user.display_name || '',
    bio: user.bio || '',
    location: user.location || '',
    is_private: user.is_private || false,
    favorite_tags: user.favorite_tags || []
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usernameUnlocked, setUsernameUnlocked] = useState(false);
  const [showUsernameConfirm, setShowUsernameConfirm] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagCategories, setTagCategories] = useState([]);
  const fileInputRef = useRef(null);

  const canChangeUsername = () => {
    if (!user.last_username_change) return true;
    const daysSinceChange = Math.floor((Date.now() - new Date(user.last_username_change).getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceChange >= 30;
  };

  const getDaysUntilUsernameChange = () => {
    if (!user.last_username_change) return 0;
    const daysSinceChange = Math.floor((Date.now() - new Date(user.last_username_change).getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - daysSinceChange);
  };

  const isUsernameChanged = () => {
    return editData.username !== user.username;
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const result = await uploadProfilePhoto(file);
    
    if (result.success) {
      // Photo automatically updated in user context
    } else {
      alert('Erreur lors de l\'upload: ' + result.error);
    }
    
    setUploading(false);
  };

  const handleSaveProfile = async () => {
    // If username is changed, show confirmation dialog
    if (isUsernameChanged() && usernameUnlocked) {
      setShowUsernameConfirm(true);
      return;
    }

    // Otherwise, save normally
    await saveProfile();
  };

  const saveProfile = async () => {
    setSaving(true);
    
    // Create update data - only include changed fields
    const updateData = {};
    
    if (editData.display_name !== (user.display_name || '')) {
      updateData.display_name = editData.display_name;
    }
    if (editData.bio !== (user.bio || '')) {
      updateData.bio = editData.bio;
    }
    if (editData.location !== (user.location || '')) {
      updateData.location = editData.location;
    }
    if (editData.is_private !== (user.is_private || false)) {
      updateData.is_private = editData.is_private;
    }
    
    // Check if favorite_tags changed
    const currentTags = user.favorite_tags || [];
    const editTags = editData.favorite_tags || [];
    if (JSON.stringify(currentTags.sort()) !== JSON.stringify(editTags.sort())) {
      updateData.favorite_tags = editData.favorite_tags;
    }
    
    // Only include username if it's actually changed and unlocked
    if (isUsernameChanged() && usernameUnlocked) {
      updateData.username = editData.username;
    }
    
    const result = await updateUserProfile(updateData);
    
    if (result.success) {
      setIsEditing(false);
      setUsernameUnlocked(false);
      setShowUsernameConfirm(false);
    } else {
      alert('Erreur lors de la sauvegarde: ' + result.error);
    }
    
    setSaving(false);
  };

  const handleUsernameUnlock = () => {
    if (!canChangeUsername()) {
      alert(`Vous devez attendre encore ${getDaysUntilUsernameChange()} jours avant de pouvoir changer votre pseudo.`);
      return;
    }
    setUsernameUnlocked(true);
  };

  const handleUsernameChange = (newUsername) => {
    setEditData({...editData, username: newUsername});
    setUsernameAvailable(null); // Reset availability check
  };

  const confirmUsernameChange = () => {
    setShowUsernameConfirm(false);
    saveProfile();
  };

  const handleCancelEdit = () => {
    setEditData({
      username: user.username,
      display_name: user.display_name || '',
      bio: user.bio || '',
      location: user.location || '',
      is_private: user.is_private || false,
      favorite_tags: user.favorite_tags || []
    });
    setIsEditing(false);
    setUsernameUnlocked(false);
    setUsernameAvailable(null);
  };

  const getInitials = (username) => {
    return username.substring(0, 2).toUpperCase();
  };

  // Load tag data on component mount for display
  useEffect(() => {
    if (user.favorite_tags && user.favorite_tags.length > 0) {
      fetchAvailableTags();
    }
  }, [user.favorite_tags]);
  useEffect(() => {
    if (isEditing) {
      fetchAvailableTags();
    }
  }, [isEditing]);

  const fetchAvailableTags = async () => {
    try {
      const [tagsResponse, categoriesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags/all-from-spots`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tag-categories`)
      ]);

      if (tagsResponse.ok && categoriesResponse.ok) {
        const tags = await tagsResponse.json();
        const categories = await categoriesResponse.json();
        setAvailableTags(tags);
        setTagCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const toggleFavoriteTag = (tagSlug) => {
    const currentTags = editData.favorite_tags || [];
    let updatedTags;

    if (currentTags.includes(tagSlug)) {
      // Remove tag
      updatedTags = currentTags.filter(t => t !== tagSlug);
    } else {
      // Add tag (check limit)
      if (currentTags.length >= 8) {
        alert('Vous ne pouvez sélectionner que 8 tags maximum');
        return;
      }
      updatedTags = [...currentTags, tagSlug];
    }

    setEditData({
      ...editData,
      favorite_tags: updatedTags
    });
  };

  const getTagCategory = (tag) => {
    return tagCategories.find(c => c.id === tag.category_id);
  };

  const getProfilePhotoUrl = () => {
    if (user.profile_photo) {
      return user.profile_photo.startsWith('http') 
        ? user.profile_photo 
        : `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`;
    }
    return null;
  };

  return (
    <Card>
      <CardContent className="p-8">
        <div className="flex items-center space-x-6">
          {/* Profile Photo */}
          <div className="relative">
            <Avatar className="h-32 w-32">
              <AvatarImage src={getProfilePhotoUrl()} alt={user.username} />
              <AvatarFallback className="text-2xl">
                {getInitials(user.username)}
              </AvatarFallback>
            </Avatar>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors disabled:opacity-50"
            >
              <Camera className="h-4 w-4" />
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="text-white text-sm">Upload...</div>
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              {isEditing ? (
                <div className="space-y-4 w-full">
                  {/* Username */}
                  <div>
                    <Label>Pseudo unique (@{user.username})</Label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Input
                          value={editData.username}
                          onChange={(e) => handleUsernameChange(e.target.value)}
                          className="text-lg pr-10"
                          placeholder="Pseudo unique"
                          disabled={!usernameUnlocked}
                        />
                        {!usernameUnlocked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleUsernameUnlock}
                            className="absolute right-1 top-1/2 transform -translate-y-1/2 p-1 h-8 w-8"
                            title="Cliquez pour déverrouiller la modification du pseudo"
                          >
                            <Lock className="h-4 w-4 text-gray-500" />
                          </Button>
                        )}
                        {usernameUnlocked && (
                          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 p-1">
                            <Unlock className="h-4 w-4 text-green-600" />
                          </div>
                        )}
                      </div>
                      {!canChangeUsername() && (
                        <div className="flex items-center text-orange-600 text-xs">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getDaysUntilUsernameChange()} j restants
                        </div>
                      )}
                    </div>
                    {!usernameUnlocked && (
                      <p className="text-xs text-gray-500 mt-1">
                        🔒 Cliquez sur le cadenas pour déverrouiller la modification
                      </p>
                    )}
                    {usernameUnlocked && !canChangeUsername() && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Vous devez attendre {getDaysUntilUsernameChange()} jours pour changer votre pseudo
                      </p>
                    )}
                    {usernameUnlocked && isUsernameChanged() && (
                      <p className="text-xs text-blue-600 mt-1">
                        ℹ️ Après validation, vous ne pourrez plus modifier ce pseudo pendant 30 jours
                      </p>
                    )}
                  </div>

                  {/* Display Name */}
                  <div>
                    <Label>Nom d'affichage</Label>
                    <Input
                      value={editData.display_name}
                      onChange={(e) => setEditData({...editData, display_name: e.target.value})}
                      className="text-xl"
                      placeholder="Votre nom d'affichage"
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={editData.bio}
                      onChange={(e) => setEditData({...editData, bio: e.target.value})}
                      placeholder="Parlez-nous de vous..."
                      rows={3}
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <Label>Localisation</Label>
                    <Input
                      value={editData.location}
                      onChange={(e) => setEditData({...editData, location: e.target.value})}
                      placeholder="Ville, région ou pays"
                    />
                  </div>

                  {/* Privacy Toggle */}
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      {editData.is_private ? (
                        <Lock className="h-5 w-5 text-gray-600" />
                      ) : (
                        <Globe className="h-5 w-5 text-blue-600" />
                      )}
                      <div>
                        <Label>Profil privé</Label>
                        <p className="text-sm text-gray-500">
                          {editData.is_private 
                            ? "Seuls vous pouvez voir vos données" 
                            : "Autres utilisateurs peuvent voir votre profil"
                          }
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={editData.is_private}
                      onCheckedChange={(checked) => setEditData({...editData, is_private: checked})}
                    />
                  </div>

                  {/* Favorite Tags Section */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="h-4 w-4 text-gray-600" />
                      <Label>Tags préférés (max 8)</Label>
                      <span className="text-xs text-gray-500">
                        {editData.favorite_tags?.length || 0}/8
                      </span>
                    </div>
                    
                    {availableTags.length > 0 ? (
                      <div className="space-y-3 max-h-48 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                        {tagCategories.map(category => {
                          const categoryTags = availableTags.filter(tag => tag.category_id === category.id);
                          if (categoryTags.length === 0) return null;
                          
                          return (
                            <div key={category.id}>
                              <div className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {categoryTags.slice(0, 10).map(tag => {
                                  const isSelected = editData.favorite_tags?.includes(tag.slug);
                                  return (
                                    <button
                                      key={tag.slug}
                                      onClick={() => toggleFavoriteTag(tag.slug)}
                                      className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                        isSelected
                                          ? 'text-white font-medium'
                                          : 'hover:opacity-80'
                                      }`}
                                      style={{
                                        backgroundColor: isSelected ? category.color : `${category.color}20`,
                                        borderColor: `${category.color}40`,
                                        color: isSelected ? 'white' : category.color
                                      }}
                                    >
                                      {isSelected && '✓'} #{tag.name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
                        Chargement des tags disponibles...
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="flex-1"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  {/* Display Mode */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {user.display_name || user.username}
                      </h1>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {user.is_private && (
                        <div className="flex items-center text-gray-600">
                          <Lock className="h-4 w-4 mr-1" />
                          <span className="text-sm">Privé</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Settings Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowAccountSettings(true)}
                      className="text-gray-500 hover:text-gray-700"
                      title="Paramètres du compte"
                    >
                      <Settings className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-lg text-gray-600">@{user.username}</p>
                    {user.bio && (
                      <p className="text-gray-700 leading-relaxed">{user.bio}</p>
                    )}
                    {user.location && (
                      <p className="text-gray-600 flex items-center">
                        📍 {user.location}
                      </p>
                    )}
                    <div className="text-sm text-gray-500">
                      Membre depuis {new Date(user.created_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </div>
                    
                    {/* Favorite Tags Display */}
                    {user.favorite_tags && user.favorite_tags.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Tags préférés</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {user.favorite_tags.map((tagSlug) => {
                            // Find tag in availableTags if loaded, otherwise show basic display
                            const tag = availableTags.find(t => t.slug === tagSlug);
                            const category = tag ? tagCategories.find(c => c.id === tag.category_id) : null;
                            
                            return (
                              <ClickableTag
                                key={tagSlug}
                                tag={tag || { slug: tagSlug, name: tagSlug }}
                                category={category}
                                size="xs"
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>

      {/* Username Change Confirmation Dialog */}
      <Dialog open={showUsernameConfirm} onOpenChange={setShowUsernameConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Confirmation du changement de pseudo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-medium text-orange-800 mb-2">
                Vous êtes sur le point de changer votre pseudo :
              </h4>
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Ancien :</span>
                  <span className="font-mono bg-gray-100 px-2 py-1 rounded">@{user.username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Nouveau :</span>
                  <span className="font-mono bg-blue-100 px-2 py-1 rounded text-blue-800">@{editData.username}</span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-800 text-sm font-medium mb-2">⚠️ Important :</p>
              <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                <li>Une fois validé, ce pseudo sera définitif pendant 30 jours</li>
                <li>Vous ne pourrez plus le modifier avant cette période</li>
                <li>Assurez-vous qu'il soit correct</li>
              </ul>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowUsernameConfirm(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={confirmUsernameChange}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {saving ? 'Validation...' : 'Confirmer le changement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Settings Modal */}
      <AccountSettingsModal
        open={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        user={user}
      />
    </Card>
  );
}